'use client';

import { useState } from 'react';
import { Baby, Heart, Sparkles, X, Footprints, Sprout, BookOpen, Globe2, Microscope } from 'lucide-react';

interface AgeVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgeSelected: (ageRange: string) => void;
}

const getAgeIcon = (value: string, size: number = 20) => {
  const iconProps = { size, color: 'white', strokeWidth: 2.5 };
  
  switch(value) {
    case 'AGE_0_1': return <Baby {...iconProps} />;
    case 'AGE_1_2': return <Footprints {...iconProps} />;
    case 'AGE_3_4': return <Sprout {...iconProps} />;
    case 'AGE_5_6': return <BookOpen {...iconProps} />;
    case 'AGE_7_8': return <Globe2 {...iconProps} />;
    case 'AGE_9_10': return <Microscope {...iconProps} />;
    default: return <Baby {...iconProps} />;
  }
};

const AGE_OPTIONS = [
  { value: 'AGE_0_1', label: '0-1 years', description: 'Infants - Sensory exposure & first words' },
  { value: 'AGE_1_2', label: '1-2 years', description: 'Toddlers - Action words & daily routines' },
  { value: 'AGE_3_4', label: '3-4 years', description: 'Preschool - Stories & social skills' },
  { value: 'AGE_5_6', label: '5-6 years', description: 'Kindergarten - Early reading & science' },
  { value: 'AGE_7_8', label: '7-8 years', description: 'Lower Elementary - Cosmic education' },
  { value: 'AGE_9_10', label: '9-10 years', description: 'Upper Elementary - Independent research' },
];

export default function AgeVerificationModal({
  isOpen,
  onClose,
  onAgeSelected,
}: AgeVerificationModalProps) {
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedAge) return;

    setIsSubmitting(true);

    try {
      // Call API to update user profile
      const response = await fetch('/api/account/kid-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kidAgeRange: selectedAge,
        }),
      });

      if (response.ok) {
        onAgeSelected(selectedAge);
        onClose();
      } else {
        console.error('Failed to update age');
        alert('Failed to save age selection. Please try again.');
      }
    } catch (error) {
      console.error('Error updating age:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflow: 'auto',
      }}
      onClick={handleSkip}
    >
      <div
        style={{
          maxWidth: '900px',
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleSkip}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <X size={20} color="white" />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Baby size={40} color="white" strokeWidth={2.5} />
          </div>
          <h2
            style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
              fontWeight: 900,
              color: 'white',
              margin: '0 0 16px 0',
              lineHeight: 1.2,
            }}
          >
            Welcome to Learning Mode!
          </h2>
          <p
            style={{
              fontSize: '1.125rem',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            To provide the best learning experience, please select your child's age range.
          </p>
        </div>

        {/* Age Options Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '32px',
          }}
        >
          {AGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedAge(option.value)}
              style={{
                background:
                  selectedAge === option.value
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(255, 255, 255, 0.1)',
                border:
                  selectedAge === option.value
                    ? '2px solid white'
                    : '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
              onMouseEnter={(e) => {
                if (selectedAge !== option.value) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedAge !== option.value) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={{ 
                background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              }}>
                {getAgeIcon(option.value, 28)}
              </div>
              <div>
                <div
                  style={{
                    color: 'white',
                    fontSize: '1.125rem',
                    fontWeight: 800,
                    marginBottom: '4px',
                  }}
                >
                  {option.label}
                </div>
                <div
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                  }}
                >
                  {option.description}
                </div>
              </div>
              {selectedAge === option.value && (
                <div
                  style={{
                    marginTop: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Sparkles size={16} />
                  Selected
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Info Box */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '32px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          }}
        >
          <Heart size={20} color="white" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.9375rem', lineHeight: 1.6 }}>
            <strong>Montessori-aligned learning:</strong> Our content is designed to match your
            child's developmental stage, following Montessori principles of self-guided, bilingual
            education. You can always adjust this later in your account settings.
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
          <button
            onClick={handleSubmit}
            disabled={!selectedAge || isSubmitting}
            style={{
              width: '100%',
              padding: '18px',
              background: selectedAge && !isSubmitting ? 'white' : 'rgba(255, 255, 255, 0.3)',
              color: selectedAge && !isSubmitting ? '#667eea' : 'rgba(255, 255, 255, 0.7)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1.125rem',
              fontWeight: 800,
              cursor: selectedAge && !isSubmitting ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              if (selectedAge && !isSubmitting) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 255, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedAge && !isSubmitting) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isSubmitting ? 'Saving...' : 'Continue to Learning'}
          </button>
          <button
            onClick={handleSkip}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            Skip for Now
          </button>
        </div>
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

